import toast from "react-hot-toast";
import { Menu } from "@headlessui/react";
import { useForm, RegisterOptions } from "react-hook-form";
import type { KeyboardEvent } from "react";
import { useUtils } from "@/app/hooks/useUtils";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { EllipsisVerticalIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useCouponCategoryAPI } from "@/app/hooks/api_hooks/coupon/useCouponCategoryAPI";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import { useCouponSubCategoryAPI } from "@/app/hooks/api_hooks/coupon/useCouponSubCategoryAPI";
import { GrFormFilter } from "react-icons/gr";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import OverbookingConfirmationModal from "@/app/components/routes/modals/OverbookingConfirmationModal";
import { useInventoryActiveTaxListAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryActiveTaxListAPI";
import { useFinanceConfigChangeListAPI } from "@/app/hooks/api_hooks/Inventory/PO/useFinanceConfigChangeListAPI";
import { useFinanceConfigChangeActionAPI } from "@/app/hooks/api_hooks/Inventory/PO/useFinanceConfigChangeActionAPI";
import { useFinanceConfigChangeUpdateAPI } from "@/app/hooks/api_hooks/Inventory/PO/useFinanceConfigChangeUpdateAPI";
import FinanceRejectRequestModal from "@/app/components/routes/auth_routes/InventoryManagement/OrderPurchaseComponent/FinanceRejectRequestModal";

interface ITaxDetails {
  cgst: number | string;
  sgst: number | string;
  igst: number | string;
  utgst: number | string;
  hsn: string;
}

interface IFinanceSKUApprovalRow {
  id: string;
  createdOn: string;
  productName: string;
  currentCategory: string;
  currentSubCategory: string;
  requestedCategory: string;
  requestedSubCategory: string;
  currentTaxDetails: ITaxDetails;
  requestedTaxDetails: ITaxDetails;
  requestedCategoryId: string;
  requestedSubCategoryId: string;
  createdBy: string;
  status: "pending" | "approved" | "denied";
}

interface IFinanceSKUApprovalApiTax {
  cgst: number;
  sgst: number;
  igst: number;
  utgst: number;
}

interface IFinanceSKUApprovalApiCategory {
  id?: string;
  category_id?: string;
  name?: string;
  category_name?: string;
  sub_category_id?: string;
  subcategory_id?: string;
  sub_category_name?: string;
  subcategory_name?: string;
}

interface IFinanceSKUApprovalApiSection {
  category?: IFinanceSKUApprovalApiCategory;
  category_subcategory_details?: IFinanceSKUApprovalApiCategory;
  outward_tax_details?: {
    hsn_code?: string;
    tax?: IFinanceSKUApprovalApiTax;
  };
}

interface IFinanceSKUApprovalApiRow {
  _id: string;
  sku_name: string;
  requested_by: string;
  current: IFinanceSKUApprovalApiSection;
  requested: IFinanceSKUApprovalApiSection;
  created_at: number;
  status: "pending" | "approved" | "rejected" | "denied";
}

interface IFinanceTaxUpdateFormValues {
  category_id: string;
  sub_category_id: string;
  hsn_code: string;
  cgst: string;
  sgst: string;
  igst: string;
  utgst: string;
}

const defaultTaxUpdateFormValues: IFinanceTaxUpdateFormValues = {
  category_id: "",
  sub_category_id: "",
  hsn_code: "",
  cgst: "",
  sgst: "",
  igst: "",
  utgst: "",
};

const mapApiDataToRows = (
  apiRows: IFinanceSKUApprovalApiRow[],
  veticDateFormat: (value: string | number | null | undefined) => string | null,
): IFinanceSKUApprovalRow[] => {
  return (apiRows ?? []).map((item) => {
    const currentCategory = item.current?.category ?? item.current?.category_subcategory_details;
    const requestedCategory = item.requested?.category ?? item.requested?.category_subcategory_details;
    return {
      id: item._id,
      createdOn: veticDateFormat(item.created_at) ?? "-",
      productName: item.sku_name,
      currentCategory: currentCategory?.name ?? currentCategory?.category_name ?? "-",
      currentSubCategory: currentCategory?.sub_category_name ?? currentCategory?.subcategory_name ?? "-",
      requestedCategory: requestedCategory?.name ?? requestedCategory?.category_name ?? "-",
      requestedSubCategory: requestedCategory?.sub_category_name ?? requestedCategory?.subcategory_name ?? "-",
      currentTaxDetails: {
        cgst: item.current?.outward_tax_details?.tax?.cgst,
        sgst: item.current?.outward_tax_details?.tax?.sgst,
        igst: item.current?.outward_tax_details?.tax?.igst,
        utgst: item.current?.outward_tax_details?.tax?.utgst,
        hsn: item.current?.outward_tax_details?.hsn_code,
      },
      requestedTaxDetails: {
        cgst: item.requested?.outward_tax_details?.tax?.cgst,
        sgst: item.requested?.outward_tax_details?.tax?.sgst,
        igst: item.requested?.outward_tax_details?.tax?.igst,
        utgst: item.requested?.outward_tax_details?.tax?.utgst,
        hsn: item.requested?.outward_tax_details?.hsn_code,
      },
      requestedCategoryId: requestedCategory?.id ?? requestedCategory?.category_id ?? "",
      requestedSubCategoryId: requestedCategory?.sub_category_id ?? requestedCategory?.subcategory_id ?? "",
      createdBy: item.requested_by,
      status: item.status === "rejected" ? "denied" : item.status,
    };
  });
};

const statusStyles: Record<IFinanceSKUApprovalRow["status"], string> = {
  pending: "bg-yellow-100 text-yellow-700",
  approved: "bg-green-100 text-green-700",
  denied: "bg-red-100 text-red-700",
};

const renderTaxDetails = (tax: ITaxDetails) => {
  return (
    <div className="leading-5">
      <p>{`CGST: ${tax.cgst}, SGST: ${tax.sgst}`}</p>
      <p>{`IGST: ${tax.igst}, UTGST: ${tax.utgst}`}</p>
      <p>{`HSN: ${tax.hsn}`}</p>
    </div>
  );
};

const FinanceSKUApprovalComponent = () => {
  const [rows, setRows] = useState<IFinanceSKUApprovalRow[]>([]);
  const [productSearch, setProductSearch] = useState("");
  const [createdByFilter, setCreatedByFilter] = useState<string>("all");
  const [taxUpdateRowId, setTaxUpdateRowId] = useState<string | null>(null);
  const [approvingRowId, setApprovingRowId] = useState<string | null>(null);
  const [actionInProgressRowId, setActionInProgressRowId] = useState<string | null>(null);
  const [isListLoading, setIsListLoading] = useState(false);
  const [isCSVExporting, setIsCSVExporting] = useState(false);
  const taxUpdateFormHook = useForm<IFinanceTaxUpdateFormValues>({
    mode: "onChange",
    defaultValues: defaultTaxUpdateFormValues,
  });
  const { execute: executeFinanceConfigChangeAction } = useFinanceConfigChangeActionAPI();
  const { execute: executeFinanceConfigChangeUpdate } = useFinanceConfigChangeUpdateAPI();
  const { execute: fetchFinanceConfigChangeList, exportCSV: exportFinanceConfigChangeListCSV } =
    useFinanceConfigChangeListAPI();
  const fetchTaxList = useInventoryActiveTaxListAPI();
  const { execute: fetchCategory, serviceCategory } = useCouponCategoryAPI();
  const { execute: fetchSubCategory, serviceSubCategory } = useCouponSubCategoryAPI();
  const { forOnlyNumbersWithDecimal } = useFormValidations();
  const { veticDateFormat } = useUtils();
  const { NO_APPOINTMENTS } = useImageConstants();
  const filterFormHook = useForm<{ createdBy: string }>({
    mode: "onChange",
    defaultValues: { createdBy: "all" },
  });
  const taxList = useAppSelector((state) => state.routes.auth.INVENTORY.state.taxList ?? []);
  const selectedTaxUpdateRow = rows.find((row) => row.id === taxUpdateRowId) ?? null;
  const isTaxUpdateModalOpen = Boolean(taxUpdateRowId);
  const selectedCategoryId = taxUpdateFormHook.watch("category_id");
  const selectedSubCategoryId = taxUpdateFormHook.watch("sub_category_id");

  const fetchSKUApprovalList = async (skuName?: string | null) => {
    setIsListLoading(true);
    const trimmedSkuName = skuName?.trim() ?? "";
    const response = await fetchFinanceConfigChangeList<IFinanceSKUApprovalApiRow>({
      status: null,
      start_date: null,
      end_date: null,
      page_size: 20,
      page_number: 1,
      sku_name: trimmedSkuName || null,
    });
    if (response?.status) {
      setRows(mapApiDataToRows(response.data ?? [], veticDateFormat));
    } else {
      setRows([]);
    }
    setIsListLoading(false);
  };

  useEffect(() => {
    fetchSKUApprovalList();
    // Intentionally run once on mount to avoid re-fetch loop from unstable hook references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProductSearchKeyDown = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    await fetchSKUApprovalList(productSearch);
  };

  const handleProductSearch = async () => {
    await fetchSKUApprovalList(productSearch);
  };

  const handleExportCSV = async () => {
    if (isCSVExporting) return;
    setIsCSVExporting(true);
    const response = await exportFinanceConfigChangeListCSV();
    if (!response?.status) {
      toast.error(response?.message || "Failed to export CSV");
    }
    setIsCSVExporting(false);
  };

  useEffect(() => {
    if (!isTaxUpdateModalOpen) return;
    fetchCategory();
    fetchTaxList();
  }, [fetchCategory, fetchTaxList, isTaxUpdateModalOpen]);

  useEffect(() => {
    if (!isTaxUpdateModalOpen || !selectedCategoryId) return;
    fetchSubCategory(selectedCategoryId);
  }, [fetchSubCategory, isTaxUpdateModalOpen, selectedCategoryId]);

  useEffect(() => {
    if (!isTaxUpdateModalOpen || !selectedTaxUpdateRow) return;
    const requestedTax = selectedTaxUpdateRow.requestedTaxDetails;
    taxUpdateFormHook.reset({
      category_id: "",
      sub_category_id: "",
      hsn_code: selectedTaxUpdateRow.requestedTaxDetails.hsn ?? "",
      cgst: `${requestedTax.cgst ?? ""}`,
      sgst: `${requestedTax.sgst ?? ""}`,
      igst: `${requestedTax.igst ?? ""}`,
      utgst: `${requestedTax.utgst ?? ""}`,
    });
  }, [isTaxUpdateModalOpen, selectedTaxUpdateRow, taxUpdateFormHook]);

  useEffect(() => {
    if (!isTaxUpdateModalOpen || !selectedTaxUpdateRow || !serviceCategory.length) return;
    const requestedCategoryName = selectedTaxUpdateRow.requestedCategory?.trim().toLowerCase();
    const requestedCategoryIdByName =
      serviceCategory.find((category) => category.category_name?.trim().toLowerCase() === requestedCategoryName)?.id ??
      "";
    const requestedCategoryId = requestedCategoryIdByName || selectedTaxUpdateRow.requestedCategoryId || "";
    if (!requestedCategoryId || taxUpdateFormHook.getValues("category_id") === requestedCategoryId) return;
    taxUpdateFormHook.setValue("category_id", requestedCategoryId, { shouldDirty: false, shouldValidate: true });
  }, [isTaxUpdateModalOpen, selectedTaxUpdateRow, serviceCategory, taxUpdateFormHook]);

  useEffect(() => {
    if (!isTaxUpdateModalOpen || !selectedTaxUpdateRow || !serviceSubCategory.length) return;
    const requestedSubCategoryName = selectedTaxUpdateRow.requestedSubCategory?.trim().toLowerCase();
    const requestedSubCategoryIdByName =
      serviceSubCategory.find(
        (subCategory) => subCategory.subcategory_name?.trim().toLowerCase() === requestedSubCategoryName,
      )?.subcategory_id ?? "";
    const requestedSubCategoryId = requestedSubCategoryIdByName || selectedTaxUpdateRow.requestedSubCategoryId || "";
    if (!requestedSubCategoryId || taxUpdateFormHook.getValues("sub_category_id") === requestedSubCategoryId) return;
    taxUpdateFormHook.setValue("sub_category_id", requestedSubCategoryId, { shouldDirty: false, shouldValidate: true });
  }, [isTaxUpdateModalOpen, selectedTaxUpdateRow, serviceSubCategory, taxUpdateFormHook]);
  const categoryListItems = useSelectMenuReducer(serviceCategory, "category_name", "id");
  const subCategoryListItems = useSelectMenuReducer(serviceSubCategory, "subcategory_name", "subcategory_id");
  const hsnCodeListItems = useSelectMenuReducer(taxList, "hsn_code", "tax_id");
  const isTaxUpdateInProgress = Boolean(taxUpdateRowId && actionInProgressRowId === taxUpdateRowId);
  const isTaxUpdateSubmitDisabled = !taxUpdateFormHook.formState.isValid || isTaxUpdateInProgress;

  const resetTaxUpdateState = () => {
    setTaxUpdateRowId(null);
    taxUpdateFormHook.reset(defaultTaxUpdateFormValues);
  };

  const handleApprove = async (rowId: string) => {
    setActionInProgressRowId(rowId);
    const response = await executeFinanceConfigChangeAction({
      request_id: rowId,
      action: "approved",
      reason: "",
      status: null,
      start_date: null,
      end_date: null,
      page_size: null,
      page_number: null,
    });
    if (!response?.status) {
      toast.error("Failed to approve request");
      setActionInProgressRowId(null);
      return;
    }
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              status: "approved",
            }
          : row,
      ),
    );
    toast.success("Request approved successfully");
    if (taxUpdateRowId === rowId) resetTaxUpdateState();
    setActionInProgressRowId(null);
  };

  const handleApproveClick = (rowId: string) => {
    setApprovingRowId(rowId);
  };

  const handleApproveConfirm = async () => {
    if (!approvingRowId) return;
    await handleApprove(approvingRowId);
    setApprovingRowId(null);
  };

  const handleUpdateRequestedDetailsClick = (rowId: string) => {
    setTaxUpdateRowId(rowId);
  };

  const handleTaxUpdate = async () => {
    if (!taxUpdateRowId) return;
    const isFormValid = await taxUpdateFormHook.trigger();
    if (!isFormValid) return;
    const values = taxUpdateFormHook.getValues();
    const categoryName = serviceCategory.find((category) => category.id === values.category_id)?.category_name ?? "";
    const subCategoryName =
      serviceSubCategory.find((subCategory) => subCategory.subcategory_id === values.sub_category_id)
        ?.subcategory_name ?? "";
    const cgst = Number(values.cgst);
    const sgst = Number(values.sgst);
    const igst = Number(values.igst);
    const utgst = Number(values.utgst);
    if (!categoryName || !subCategoryName) {
      toast.error("Please select category and sub category");
      return;
    }
    setActionInProgressRowId(taxUpdateRowId);
    const response = await executeFinanceConfigChangeUpdate({
      request_id: taxUpdateRowId,
      outward_tax_details: {
        hsn_code: values.hsn_code.trim(),
        tax: {
          cgst,
          sgst,
          igst,
          utgst,
        },
      },
      category_subcategory_details: {
        id: values.category_id,
        name: categoryName,
        sub_category_id: values.sub_category_id,
        sub_category_name: subCategoryName,
      },
    });
    if (!response?.status) {
      toast.error(response?.message || "Failed to update request");
      setActionInProgressRowId(null);
      return;
    }
    setRows((prev) =>
      prev.map((row) =>
        row.id === taxUpdateRowId
          ? {
              ...row,
              status: "approved",
            }
          : row,
      ),
    );
    resetTaxUpdateState();
    setActionInProgressRowId(null);
    await fetchSKUApprovalList(productSearch);
  };

  const triggerTaxValidation = () => {
    void taxUpdateFormHook.trigger(["cgst", "sgst", "igst", "utgst"]);
  };

  const validateTaxPair = () => {
    const cgst = Number(taxUpdateFormHook.getValues("cgst"));
    const sgst = Number(taxUpdateFormHook.getValues("sgst"));
    if (Number.isNaN(cgst) || Number.isNaN(sgst)) return true;
    return cgst === sgst || "CGST and SGST must be same";
  };

  const validateTaxSum = () => {
    const cgst = Number(taxUpdateFormHook.getValues("cgst"));
    const sgst = Number(taxUpdateFormHook.getValues("sgst"));
    const igst = Number(taxUpdateFormHook.getValues("igst"));
    const utgst = Number(taxUpdateFormHook.getValues("utgst"));
    if ([cgst, sgst, igst, utgst].some((field) => Number.isNaN(field))) return true;
    const total = cgst + sgst;
    return (igst === total && utgst === total) || "IGST and UTGST must equal CGST + SGST";
  };
  
  const taxPercentageValidations: RegisterOptions<IFinanceTaxUpdateFormValues> = {
    required: "Required",
    ...(forOnlyNumbersWithDecimal.validations as RegisterOptions<IFinanceTaxUpdateFormValues>),
    max: {
      value: 50,
      message: "Tax % cannot be greater than 50",
    },
  };

  const createdByFilterItems = useMemo(() => {
    const uniqueCreatedBy = Array.from(new Set(rows.map((row) => row.createdBy).filter(Boolean)));
    return [{ id: "all", title: "All" }, ...uniqueCreatedBy.map((value) => ({ id: value, title: value }))];
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (createdByFilter === "all") return rows;
    return rows.filter((row) => row.createdBy === createdByFilter);
  }, [rows, createdByFilter]);

  const taxUpdateModalContent = (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Category</label>
          <SearchableSelectMenu
            errorMessages={[{ message: "Category is required", type: "required" }]}
            onSelectItem={(item) => {
              taxUpdateFormHook.setValue("category_id", item?.id ?? "", { shouldDirty: true, shouldValidate: true });
              taxUpdateFormHook.setValue("sub_category_id", "", { shouldDirty: true, shouldValidate: true });
            }}
            fieldError={taxUpdateFormHook.formState.errors.category_id}
            register={taxUpdateFormHook.register("category_id", { required: true })}
            selectItems={categoryListItems}
            placeholder="Select Category"
            showTooltips
            showTypedErrors
            showDropdownIcon
            withDropDownSearch
            defaultSelected={
              categoryListItems?.find((item) => item.id === selectedCategoryId) ??
              categoryListItems?.find(
                (item) =>
                  item.title?.trim().toLowerCase() === selectedTaxUpdateRow?.requestedCategory?.trim().toLowerCase(),
              )
            }
            listBoxClassName="w-full"
            className="text-gray-800"
            containerClassName="w-full"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Sub Category</label>
          <SearchableSelectMenu
            errorMessages={[{ message: "Sub-category is required", type: "required" }]}
            onSelectItem={(item) => {
              taxUpdateFormHook.setValue("sub_category_id", item?.id ?? "", {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            fieldError={taxUpdateFormHook.formState.errors.sub_category_id}
            register={taxUpdateFormHook.register("sub_category_id", { required: true })}
            selectItems={subCategoryListItems}
            placeholder="Select Sub-Category"
            showTooltips
            showTypedErrors
            showDropdownIcon
            withDropDownSearch
            defaultSelected={
              subCategoryListItems?.find((item) => item.id === selectedSubCategoryId) ??
              subCategoryListItems?.find(
                (item) =>
                  item.title?.trim().toLowerCase() === selectedTaxUpdateRow?.requestedSubCategory?.trim().toLowerCase(),
              )
            }
            listBoxClassName="w-full"
            className="text-gray-800"
            containerClassName="w-full"
            disabled={!selectedCategoryId}
          />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-700">HSN Code</label>
        <SearchableSelectMenu
          errorMessages={[{ message: "HSN Code is required", type: "required" }]}
          onSelectItem={(item) => {
            taxUpdateFormHook.setValue("hsn_code", item?.title ?? "", { shouldDirty: true, shouldValidate: true });
          }}
          fieldError={taxUpdateFormHook.formState.errors.hsn_code}
          register={taxUpdateFormHook.register("hsn_code", { required: true })}
          selectItems={hsnCodeListItems}
          placeholder="Select HSN Code"
          showTooltips
          showTypedErrors
          showDropdownIcon
          withDropDownSearch
          defaultSelected={hsnCodeListItems?.find((item) => item.title === taxUpdateFormHook.watch("hsn_code"))}
          listBoxClassName="w-full"
          className="text-gray-800"
          containerClassName="w-full"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">CGST</label>
          <input
            type="text"
            className={
              taxUpdateFormHook.formState.errors.cgst
                ? "w-full rounded-md border border-red-600 px-3 py-2 text-xs"
                : "w-full rounded-md border border-gray-300 px-3 py-2 text-xs"
            }
            placeholder="Enter CGST"
            {...taxUpdateFormHook.register("cgst", {
              ...taxPercentageValidations,
              validate: {
                taxPair: validateTaxPair,
                taxSum: validateTaxSum,
              },
              onChange: triggerTaxValidation,
            })}
          />
          {taxUpdateFormHook.formState.errors.cgst ? (
            <p className="mt-0.5 text-[10px] text-red-500">{taxUpdateFormHook.formState.errors.cgst.message}</p>
          ) : null}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">SGST</label>
          <input
            type="text"
            className={
              taxUpdateFormHook.formState.errors.sgst
                ? "w-full rounded-md border border-red-600 px-3 py-2 text-xs"
                : "w-full rounded-md border border-gray-300 px-3 py-2 text-xs"
            }
            placeholder="Enter SGST"
            {...taxUpdateFormHook.register("sgst", {
              ...taxPercentageValidations,
              validate: {
                taxPair: validateTaxPair,
                taxSum: validateTaxSum,
              },
              onChange: triggerTaxValidation,
            })}
          />
          {taxUpdateFormHook.formState.errors.sgst ? (
            <p className="mt-0.5 text-[10px] text-red-500">{taxUpdateFormHook.formState.errors.sgst.message}</p>
          ) : null}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">IGST</label>
          <input
            type="text"
            className={
              taxUpdateFormHook.formState.errors.igst
                ? "w-full rounded-md border border-red-600 px-3 py-2 text-xs"
                : "w-full rounded-md border border-gray-300 px-3 py-2 text-xs"
            }
            placeholder="Enter IGST"
            {...taxUpdateFormHook.register("igst", {
              ...taxPercentageValidations,
              validate: {
                taxSum: validateTaxSum,
              },
              onChange: triggerTaxValidation,
            })}
          />
          {taxUpdateFormHook.formState.errors.igst ? (
            <p className="mt-0.5 text-[10px] text-red-500">{taxUpdateFormHook.formState.errors.igst.message}</p>
          ) : null}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">UTGST</label>
          <input
            type="text"
            className={
              taxUpdateFormHook.formState.errors.utgst
                ? "w-full rounded-md border border-red-600 px-3 py-2 text-xs"
                : "w-full rounded-md border border-gray-300 px-3 py-2 text-xs"
            }
            placeholder="Enter UTGST"
            {...taxUpdateFormHook.register("utgst", {
              ...taxPercentageValidations,
              validate: {
                taxSum: validateTaxSum,
              },
              onChange: triggerTaxValidation,
            })}
          />
          {taxUpdateFormHook.formState.errors.utgst ? (
            <p className="mt-0.5 text-[10px] text-red-500">{taxUpdateFormHook.formState.errors.utgst.message}</p>
          ) : null}
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full">
      <div className="rounded-lg bg-white px-2 pb-0.5">
        <div className="pb-2">
          <div className="mt-2 flex items-center justify-between gap-2">
            <p className="text-sm font-semibold">Financial SKU Approval</p>
            <button
              type="button"
              onClick={handleExportCSV}
              disabled={isCSVExporting}
              className="rounded-md bg-black px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isCSVExporting ? "Exporting..." : "Export as CSV"}
            </button>
          </div>
          <InputWithStartAndEndAddOn
            value={productSearch}
            containerClassName="mt-2 w-full max-w-sm"
            placeholder="Search by Product Name"
            onChange={(event) => setProductSearch(event.target.value)}
            onKeyDown={handleProductSearchKeyDown}
            endAddon={
              <button
                type="button"
                onClick={handleProductSearch}
                className="font-semibold text-xs cursor-pointer"
                data-table-stop-row-click="true"
              >
                <MagnifyingGlassIcon className="w-4 h-4 hover:text-blue-500 hover:scale-105 transition-transform" />
              </button>
            }
            startAddon={<span className="font-semibold text-xs cursor-pointer">SKU</span>}
          />
        </div>
      </div>
      <div className="mt-2 scroll-inner max-h-[calc(100vh-260px)] overscroll-y-contain rounded-xl bg-[#F9FAFB] overflow-auto">
        <div className="w-full">
          <div className="inline-block min-w-full align-middle">
            <table className="w-full min-w-[1100px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
              <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-20">
                <tr>
                  <th className="p-2 py-3 text-left text-[11px] sm:text-xs whitespace-nowrap font-medium text-black tracking-wider">
                    Created on
                  </th>
                  <th className="p-2 py-3 text-left text-[11px] sm:text-xs whitespace-nowrap font-medium text-black tracking-wider">
                    Product Name
                  </th>
                  <th className="p-2 py-3 text-left text-[11px] sm:text-xs font-medium text-black tracking-wider">
                    Current Category &amp; Sub category
                  </th>
                  <th className="p-2 py-3 text-left text-[11px] sm:text-xs font-medium text-black tracking-wider">
                    Requested Category &amp; Sub category
                  </th>
                  <th className="p-2 py-3 text-left text-[11px] sm:text-xs whitespace-nowrap font-medium text-black tracking-wider">
                    Current Tax details
                  </th>
                  <th className="p-2 py-3 text-left text-[11px] sm:text-xs whitespace-nowrap font-medium text-black tracking-wider">
                    Requested Tax details
                  </th>
                  <th className="p-2 py-3 text-left text-[11px] sm:text-xs whitespace-nowrap font-medium text-black tracking-wider">
                    <SearchableSelectMenu
                      onSelectItem={(item) => {
                        setCreatedByFilter(item?.id ?? "all");
                      }}
                      asButton
                      renderAsButton={
                        <button type="button" className="flex items-center gap-1 text-[11px] sm:text-xs font-medium">
                          Created By
                          <GrFormFilter className="w-4 h-4" />
                        </button>
                      }
                      register={filterFormHook.register("createdBy")}
                      selectItems={createdByFilterItems}
                      containerClassName="w-52"
                      fieldError={filterFormHook.formState.errors.createdBy}
                      errorMessages={[]}
                    />
                  </th>
                  <th className="p-2 py-3 text-left text-[11px] sm:text-xs whitespace-nowrap font-medium text-black tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="text-[11px] sm:text-xs">
                {isListLoading ? (
                  <tr className="bg-white">
                    <td colSpan={8} className="px-2 py-10 text-center text-xs text-gray-500 rounded-2xl">
                      Loading requests...
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr className="bg-white">
                    <td colSpan={8} className="rounded-2xl">
                      <div className="flex flex-col items-center justify-center h-full gap-4 py-10">
                        <img
                          src={NO_APPOINTMENTS}
                          alt="No data"
                          className="w-32 h-24 sm:w-48 sm:h-32 object-contain"
                        />
                        <p className="text-sm sm:text-base font-medium text-[#6C727F]">
                          No SKU approval requests found.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row, index) => {
                    const isFirst = index === 0;
                    const isLast = index === filteredRows.length - 1;
                    const isSingle = filteredRows.length === 1;
                    const showActionsMenu = row.status === "pending";
                    const showStatusChip = row.status !== "pending";
                    return (
                      <tr
                        key={row.id}
                        className={`
                          bg-white
                          ${isSingle ? "rounded-xl" : ""}
                          ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                          ${isLast && !isSingle ? "rounded-b-xl" : ""}
                        `}
                      >
                        <td
                          className={`
                            p-2 whitespace-nowrap text-black
                            ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                          `}
                        >
                          {row.createdOn}
                        </td>
                        <td className="p-2 text-black">
                          <p className="min-w-[140px] break-words">{row.productName}</p>
                        </td>
                        <td className="p-2 text-black">
                          <div className="min-w-[150px] leading-5">
                            <p>{row.currentCategory}</p>
                            <p className="text-[#6C727F]">{row.currentSubCategory}</p>
                          </div>
                        </td>
                        <td className="p-2 text-black">
                          <div className="min-w-[150px] leading-5">
                            <p>{row.requestedCategory}</p>
                            <p className="text-[#6C727F]">{row.requestedSubCategory}</p>
                          </div>
                        </td>
                        <td className="p-2 text-black">
                          <div className="min-w-[160px]">{renderTaxDetails(row.currentTaxDetails)}</div>
                        </td>
                        <td className="p-2 text-black">
                          <div className="min-w-[160px]">{renderTaxDetails(row.requestedTaxDetails)}</div>
                        </td>
                        <td className="p-2 whitespace-nowrap text-black">{row.createdBy}</td>
                        <td
                          className={`
                            p-2 text-black
                            ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                          `}
                        >
                          <div className="flex flex-col items-center gap-2">
                            {showStatusChip ? (
                              <span
                                className={`w-fit rounded-[5px] px-2 py-1 text-[11px] capitalize ${statusStyles[row.status]}`}
                              >
                                {row.status}
                              </span>
                            ) : null}
                            {showActionsMenu ? (
                              <Menu as="div" className="relative inline-block text-left">
                                <Menu.Button
                                  type="button"
                                  data-table-stop-row-click="true"
                                  disabled={actionInProgressRowId === row.id}
                                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  <EllipsisVerticalIcon className="h-5 w-5" />
                                </Menu.Button>
                                <Menu.Items className="absolute right-0 z-30 mt-1 w-28 rounded-md border border-gray-200 bg-white p-1 shadow-lg focus:outline-none">
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        type="button"
                                        data-table-stop-row-click="true"
                                        className={`w-full rounded px-2 py-1 text-left text-xs ${active ? "bg-gray-100" : ""}`}
                                        onClick={() => handleApproveClick(row.id)}
                                      >
                                        Approve
                                      </button>
                                    )}
                                  </Menu.Item>
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        type="button"
                                        data-table-stop-row-click="true"
                                        className={`w-full rounded px-2 py-1 text-left text-xs ${active ? "bg-gray-100" : ""}`}
                                        onClick={() => handleUpdateRequestedDetailsClick(row.id)}
                                      >
                                        Deny (Modify)
                                      </button>
                                    )}
                                  </Menu.Item>
                                </Menu.Items>
                              </Menu>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <OverbookingConfirmationModal
        open={Boolean(approvingRowId)}
        title="Approve Request"
        message="Are you sure you want to approve this request?"
        onClose={() => {
          setApprovingRowId(null);
        }}
        onConfirm={handleApproveConfirm}
        confirmText="Yes"
        cancelText="Cancel"
        confirmDisabled={Boolean(approvingRowId && actionInProgressRowId === approvingRowId)}
      />
      <FinanceRejectRequestModal
        open={isTaxUpdateModalOpen}
        setOpen={(value) => {
          if (!value) {
            resetTaxUpdateState();
          }
        }}
        title="Update Requested Tax Details"
        submitLabel={isTaxUpdateInProgress ? "Updating..." : "Update"}
        panelClassName="sm:w-[520px]"
        customContent={taxUpdateModalContent}
        onCustomSubmit={handleTaxUpdate}
        isSubmitDisabled={isTaxUpdateSubmitDisabled}
      />
    </div>
  );
};

export default FinanceSKUApprovalComponent;
